<template>
  <div class="d-flex" ref="navigationBarWrapper">
    <div :title="$t('fastBackward')">
      <button
        data-test-id="fast-backward"
        class="btn btn-secondary me-1 flip nav-epoch-btn"
        :class="getButtonClass(isPreviousDisabled, speed < 0)"
        :disabled="isPreviousDisabled"
        @click="fastForwardPreviousEpoch"
      >
        <font-awesome-icon icon="fa-solid fa-forward" />
      </button>
    </div>

    <div :title="$t('movePrevious')">
      <button
        data-test-id="step-backward"
        class="btn btn-secondary me-1 flip nav-epoch-btn"
        :class="getButtonClass(isPreviousDisabled, false)"
        :disabled="isPreviousDisabled"
        @click="previousEpoch(false)"
      >
        <font-awesome-icon icon="fa-solid fa-play" />
      </button>
    </div>

    <div :title="$t('pause')">
      <button
        data-test-id="pause"
        class="btn btn-secondary me-1 nav-epoch-btn"
        :class="getButtonClass(!isSpeedActive || isFullNight, speed === 0)"
        :disabled="!isSpeedActive || isFullNight"
        @click="pauseEpoch"
      >
        <font-awesome-icon icon="fa-solid fa-pause" />
      </button>
    </div>

    <div :title="$t('moveForward')">
      <button
        data-test-id="step-forward"
        class="btn btn-secondary me-1 nav-epoch-btn"
        :class="getButtonClass(isForwardDisabled, false)"
        :disabled="isForwardDisabled"
        @click="nextEpoch(false)"
      >
        <font-awesome-icon icon="fa-solid fa-play" />
      </button>
    </div>

    <div :title="$t('fastForward')">
      <button
        data-test-id="fast-forward"
        class="btn btn-secondary me-1 nav-epoch-btn"
        :class="getButtonClass(isForwardDisabled, speed > 0)"
        :disabled="isForwardDisabled"
        @click="fastForwardNextEpoch"
      >
        <font-awesome-icon icon="fa-solid fa-forward" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { router } from '@/router';
import { SafeAny } from '@/common/types';
import { getEpochUrl } from '../../get-epoch-url';
import { capitalizeFirstLetter } from '@/common/utils';
import { tryGranularStep } from '../../granular-step-guard';

const WHEEL_PIXELS_PER_STEP = 100;
const WHEEL_MAX_STEPS_PER_EVENT = 6;
// Per-event magnitude that separates a discrete wheel notch from a fragment
// of an OS-level smooth-scroll burst. Standard wheel notches report
// |deltaY| ≥ 100 (Windows/macOS) or ≥ 53 (some Linux configs / driver-
// customised mice). macOS smooth-scroll and trackpad fragments arrive at
// ~4–20 px per event in a stream of ~16 ms-spaced events. Setting the
// threshold at 50 cleanly separates the two: discrete notches bypass the
// 600 ms granular-step gate (so rapid notching produces rapid steps),
// fragments go through the gate (so a 15-event macOS burst per physical
// notch collapses to a single step).
const WHEEL_DISCRETE_NOTCH_MIN = 50;
// Normalisation factors when the browser reports deltaY in units other than
// pixels (rare on Edge/Chrome but specified by the WheelEvent.deltaMode API).
const WHEEL_LINE_TO_PIXEL = 16;
const WHEEL_PAGE_TO_PIXEL = 800;

export default defineComponent({
  name: 'NavEpochProgression',
  data() {
    return {
      interval: undefined as SafeAny,
      keyHandler: null as ((e: KeyboardEvent) => void) | null,
      wheelHandler: null as ((e: WheelEvent) => void) | null,
      limits: { backward: 1, forward: 1 },
      seconds: 2000,
      speed: 0
    };
  },
  computed: {
    settings() {
      return this.$store.state.epoch.settings;
    },
    hypnogramStart(): number | undefined {
      return this.$store.state.study?.hypnogram?.data?.start;
    },
    lightsOffEpoch(): number | '' | null {
      const lightsOffTime = this.$store.state.study?.hypnogram?.data?.lights?.[0]?.x;

      if (!lightsOffTime || !this.hypnogramStart || !this.epochSize) {
        return null;
      }

      const epoch = Math.floor((lightsOffTime - this.hypnogramStart) / this.epochSize);

      return epoch;
    },
    lightsOnEpoch(): number | '' {
      const lightsOnTime = this.$store.state.study?.hypnogram?.data?.lights?.[1]?.x;

      if (!lightsOnTime || this.hypnogramStart == null || !this.epochSize) return '';

      const diff = lightsOnTime - this.hypnogramStart;
      const epoch = Math.floor(diff / this.epochSize);

      return diff % this.epochSize === 0 || this.epochSize === 30 ? epoch - 1 : epoch;
    },
    isAtLightsOff(): boolean {
      const selectedEpoch = parseInt(this.epochSelected as string, 10);
      return (
        typeof this.lightsOffEpoch === 'number' &&
        !Number.isNaN(selectedEpoch) &&
        selectedEpoch <= this.lightsOffEpoch
      );
    },
    isAtLightsOn(): boolean {
      const selectedEpoch = parseInt(this.epochSelected as string, 10);
      return (
        typeof this.lightsOnEpoch === 'number' &&
        !Number.isNaN(selectedEpoch) &&
        selectedEpoch >= this.lightsOnEpoch
      );
    },
    epochSize(): number {
      return parseInt(this.$route.params.epochSize as string);
    },
    epochSelected() {
      return this.$route.params.epochSelected;
    },
    isFirstEpoch(): boolean {
      return this.epochSelected === '0';
    },
    isSpeedActive(): boolean {
      return !!this.speed;
    },
    isFullNight(): boolean {
      return this.$route.params.epochSize === '-1';
    },
    scoreSetData() {
      return this.$store.getters['scoreSet/getScoreSet'];
    },
    isPreviousDisabled() {
      return this.isAtLightsOff || this.isFullNight;
    },
    isForwardDisabled() {
      return this.isAtLightsOn || this.isFullNight;
    }
  },
  methods: {
    keyboardNavigation(event: KeyboardEvent): void {
      if (
        this.$store.state.study.config.isLoading ||
        this.$store.getters['epoch/isEpochViewLoading'] ||
        this.$store.getters['epoch/getSettingDialog']('visibility')
      ) {
        return;
      }

      const offset: number = (this.$store.state.epoch as SafeAny).granularOffsetSec ?? 0;

      if (event.shiftKey && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        if (event.repeat) return;
        event.preventDefault();
        this.shiftGranularNavigate(event.key === 'ArrowRight' ? 1 : -1);
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'PageUp') {
        if (offset < 0) {
          this.$store.commit('epoch/setGranularOffset', 0);
          return;
        }
        this.nextEpoch(false);
      } else if (event.key === 'ArrowLeft' || event.key === 'PageDown') {
        if (offset > 0) {
          this.$store.commit('epoch/setGranularOffset', 0);
          return;
        }
        this.previousEpoch(false);
      } else if (event.key === 'Home') {
        this.$store.commit('epoch/setGranularOffset', 0);
        this.toEpoch(this.lightsOffEpoch as number);
      } else if (event.key === 'End') {
        this.$store.commit('epoch/setGranularOffset', 0);
        this.toEpoch(this.lightsOnEpoch as number);
      }
    },
    applyGranularStep(direction: 1 | -1): void {
      const currentOffset: number = (this.$store.state.epoch as SafeAny).granularOffsetSec ?? 0;

      if (direction === 1 && this.isAtLightsOn && currentOffset >= 0) return;

      if (direction === -1 && this.isAtLightsOff && currentOffset <= 0) return;

      const size = this.epochSize;
      const step = size * 0.2;
      const newOffset = currentOffset + direction * step;

      if (newOffset >= size) {
        this.nextEpoch(false);
      } else if (newOffset <= -size) {
        this.previousEpoch(false);
      } else {
        this.$store.commit('epoch/setGranularOffset', newOffset);
      }
    },
    shiftGranularNavigate(direction: 1 | -1): void {
      if (this.isFullNight) return;
      this.applyGranularStep(direction);
    },
    // Apply N granular steps in a direction, with overshoot logic for smooth
    // boundary crossings. Extracted so both the sustained-scroll path and the
    // burst-end timer can call it.
    applyWheelSteps(direction: 1 | -1, steps: number): void {
      if (steps <= 0 || this.isFullNight || !this.epochSize || this.epochSize === -1) return;

      const size = this.epochSize;
      const step = size * 0.2;
      const currentEpoch = parseInt(this.epochSelected as string, 10);
      const currentOffset: number = (this.$store.state.epoch as SafeAny).granularOffsetSec ?? 0;

      let newEpoch = currentEpoch;
      let newOffset = currentOffset + direction * step * steps;

      while (newOffset >= size) {
        newEpoch += 1;
        newOffset -= size;
      }
      while (newOffset <= -size) {
        newEpoch -= 1;
        newOffset += size;
      }

      if (typeof this.lightsOnEpoch === 'number' && newEpoch >= this.lightsOnEpoch) {
        newEpoch = this.lightsOnEpoch;
        if (newOffset > 0) newOffset = 0;
      }
      if (typeof this.lightsOffEpoch === 'number' && newEpoch <= this.lightsOffEpoch) {
        newEpoch = this.lightsOffEpoch;
        if (newOffset < 0) newOffset = 0;
      }

      if (newEpoch === currentEpoch && newOffset === currentOffset) return;

      if (newEpoch !== currentEpoch) {
        // Reserve the granular guard so the post-navigation watcher's offset reset doesn't fight the wheel.
        tryGranularStep();
        // SMOOTH SLIDE ACROSS THE BOUNDARY:
        // effectiveEpoch = base + offset/size, so (currentEpoch, overshoot)
        // and (newEpoch, newOffset) render IDENTICALLY when
        //   overshoot = (newEpoch - currentEpoch) * size + newOffset.
        const overshootOffset = (newEpoch - currentEpoch) * size + newOffset;
        this.$store.commit('epoch/setGranularOffset', overshootOffset);
        this.$store.commit('epoch/setPendingGranularOffset', newOffset);
        this.toEpoch(newEpoch);
      } else {
        this.$store.commit('epoch/setGranularOffset', newOffset);
      }
    },
    handleWheelScroll(event: WheelEvent): void {
      if (this.isFullNight || !this.epochSize || this.epochSize === -1) return;
      if (
        this.$store.state.study.config.isLoading ||
        this.$store.getters['epoch/isEpochViewLoading'] ||
        this.$store.getters['epoch/getSettingDialog']('visibility')
      )
        return;

      const pageScrollable = document.documentElement.scrollHeight > window.innerHeight + 10;
      const isGranularIntent = !pageScrollable || event.shiftKey;

      if (!isGranularIntent) return;

      const rawDelta = event.deltaY !== 0 ? event.deltaY : event.deltaX;
      if (rawDelta === 0) return;

      event.preventDefault();

      // Normalise delta across browsers that report in lines (deltaMode=1)
      // or pages (deltaMode=2). Edge/Chrome on macOS+Windows almost always
      // report deltaMode=0 (pixels), but the WheelEvent spec allows the
      // others — handle them so a misbehaving driver can't silently drop us
      // back to zero-step output.
      const unit =
        event.deltaMode === 1
          ? WHEEL_LINE_TO_PIXEL
          : event.deltaMode === 2
            ? WHEEL_PAGE_TO_PIXEL
            : 1;
      const pixelDelta = rawDelta * unit;
      const direction: 1 | -1 = pixelDelta > 0 ? 1 : -1;
      const magnitude = Math.abs(pixelDelta);

      // Discriminate discrete wheel notches from smooth-scroll burst
      // fragments by per-event magnitude. See WHEEL_DISCRETE_NOTCH_MIN
      // comment above for the rationale. Discrete events bypass the
      // tryGranularStep gate so rapid Windows-style notching produces rapid
      // steps; fragment events go through the gate so a macOS smooth-scroll
      // burst of ~15 small events collapses to one step per physical notch.
      const isDiscreteNotch = magnitude >= WHEEL_DISCRETE_NOTCH_MIN;
      if (!isDiscreteNotch) {
        // Smooth-scroll fragment: only the first event of the burst gets
        // through; the remaining events of the burst are blocked by the
        // 600 ms gate the first one reserves. macOS user with deltaY ≈ −4
        // sees one step per physical notch instead of either nothing
        // (previous accumulator approach) or 3–4 steps (the min-1 approach).
        if (!tryGranularStep()) return;
      }

      // Step count from this single event's delta.
      //   - Standard Windows notch (deltaY = 100): round(100/100) = 1 step.
      //   - Hard sweep with deltaY = 300+ in one event: round(300/100) = 3
      //     steps — fast users get proportional movement.
      //   - Smooth-scroll fragment (deltaY ≈ 4) that passed the gate above:
      //     Math.max(1, …) guarantees a single step.
      const steps = Math.min(
        WHEEL_MAX_STEPS_PER_EVENT,
        Math.max(1, Math.round(magnitude / WHEEL_PIXELS_PER_STEP))
      );

      this.applyWheelSteps(direction, steps);
    },
    nextEpoch(keepSpeed: boolean) {
      const curr = parseInt(this.epochSelected as string);

      if (this.isAtLightsOn || (this.isSpeedActive && !keepSpeed)) {
        this.pauseEpoch();
        return;
      }

      const to = Math.min(curr + 1, this.lightsOnEpoch as number);
      this.toEpoch(to);
    },
    previousEpoch(keepSpeed: boolean) {
      const curr = parseInt(this.epochSelected as string, 10);

      if (this.isAtLightsOff || (this.isSpeedActive && !keepSpeed)) {
        this.pauseEpoch();
        return;
      }

      const to = Math.max(curr - 1, this.lightsOffEpoch as number);
      this.toEpoch(to);
    },
    toEpoch(count: number): void {
      const { id, epochSize } = this.$route.params;
      const { band, notch } = this.settings;

      if (epochSize === '-1') return; // Full night view navigation block

      const newPath = getEpochUrl(
        id as string,
        capitalizeFirstLetter(notch),
        capitalizeFirstLetter(band),
        epochSize as string,
        count
      );
      router.push({ path: newPath, query: { ...this.$route.query } });
    },
    startCount() {
      if (this.speed) {
        this.speed > 0 ? this.nextEpoch(true) : this.previousEpoch(true);
        if (!this.interval) {
          this.interval = setInterval(
            () => (this.speed > 0 ? this.nextEpoch(true) : this.previousEpoch(true)),
            this.seconds / Math.abs(this.speed)
          );
        }
      } else {
        clearInterval(this.interval);
        this.interval = undefined;
      }
    },
    fastForwardNextEpoch(): void {
      if (this.isAtLightsOn) {
        this.pauseEpoch();
        return;
      }
      if (this.speed < this.limits.forward) {
        this.speed++;
        this.startCount();
      }
    },
    fastForwardPreviousEpoch(): void {
      if (this.isAtLightsOff) {
        this.pauseEpoch();
        return;
      }
      if (this.speed > -this.limits.backward) {
        this.speed--;
        this.startCount();
      }
    },
    pauseEpoch(): void {
      this.speed = 0;
      clearInterval(this.interval);
      this.interval = undefined;
    },
    handleOutsideClick(event: MouseEvent): void {
      const wrapper = this.$el as HTMLElement;

      if (wrapper && !wrapper.contains(event.target as Node)) {
        this.pauseEpoch();
      }
    },
    getButtonClass(isDisabled: boolean, isActive: boolean): string {
      if (isDisabled) return 'btn-disabled';
      if (isActive) return 'btn-active';

      return 'btn-inactive';
    }
  },
  watch: {
    lightsOffEpoch: {
      immediate: true,
      handler(newVal: number | '' | null) {
        if (typeof newVal !== 'number' || this.isFullNight) return;

        const curr = parseInt(this.epochSelected as string, 10);

        if (!Number.isNaN(curr) && curr < newVal) {
          this.toEpoch(newVal);
        }
      }
    }
  },
  mounted() {
    this.keyHandler = this.keyboardNavigation.bind(this);
    this.wheelHandler = this.handleWheelScroll.bind(this);
    document.addEventListener('keydown', this.keyHandler);
    document.addEventListener('wheel', this.wheelHandler, { passive: false });
    window.addEventListener('click', this.handleOutsideClick, true);
  },
  beforeUnmount() {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
    }
    if (this.wheelHandler) {
      document.removeEventListener('wheel', this.wheelHandler);
    }
    window.removeEventListener('click', this.handleOutsideClick, true);
    this.pauseEpoch();
  }
});
</script>
<style lang="scss" scoped>
@use './nav-epoch-progression.style.scss';
</style>
